import React from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Clock as ClockIcon,
  Info,
  Paperclip as PaperclipIcon,
} from 'react-feather'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Cancel as CancelIcon } from '@material-ui/icons'
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core'

import { ButtonGroup, JoditEditor } from 'components'

import helpers from 'helpers'

import useAuth from 'hooks/useAuth'

import theme from 'theme'
import styles from './styles'
import constants from 'constants/index'

const TextBox = ({
  form = 'text-box-form',
  attachmentControlName = 'attachments',
  controlName = 'statusId',
  buttonItems = [],
  common = false,
  subTitle,
  deadlineMessage,
  infoTooltip,
  children,
  disabled = false,
  ...rest
}) => {
  const { avatarLetters } = helpers
  const { user } = useAuth()
  const { control, errors, setValue, watch } = useFormContext()

  const useStyles = styles({ errors, attachmentControlName })
  const classes = useStyles()

  const smDown = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: false,
  })

  const attachments = watch(attachmentControlName)

  const handleFileChange = (event) => {
    const newFiles = Array.from(event?.target?.files || [])
    setValue(attachmentControlName, [...attachments, ...newFiles])

    event.target.value = ''
  }

  const handleRemoveFile = (fileName) => {
    const updatedFiles = attachments?.filter((file) => file?.name !== fileName)
    setValue(attachmentControlName, updatedFiles)
  }

  return (
    <Box {...rest}>
      <Paper elevation={1} className={classes.container}>
        {children}
        <Box display="flex" flexDirection="column" px={2} pt={2}>
          <Box width="100%">
            <Controller
              control={control}
              name="content"
              as={
                common ? (
                  <TextField
                    error={!!errors?.content}
                    helperText={errors?.content?.message}
                    fullWidth
                    multiline
                    minRows={10}
                    maxRows={10}
                    variant="outlined"
                    disabled={disabled}
                  />
                ) : (
                  <JoditEditor.Editor
                    error={!!errors?.content}
                    helperText={errors?.content?.message}
                    disabled={disabled}
                  />
                )
              }
              mode="onBlur"
            />
          </Box>
          <PerfectScrollbar
            options={{ wheelPropagation: false, useBothWheelAxes: true }}
          >
            <Box display="flex" flexDirection="row" pb={2}>
              {attachments?.map((file, index) => (
                <Box key={index} mr={2} pt={2}>
                  <Button type="button" className={classes.styledButton}>
                    <Typography className={classes.nameFile}>
                      {file?.name}
                    </Typography>
                    <Typography
                      className={classes.cancelIcon}
                      onClick={() => handleRemoveFile(file?.name)}
                    >
                      <CancelIcon fontSize="small" />
                    </Typography>
                  </Button>
                </Box>
              ))}
            </Box>
          </PerfectScrollbar>
        </Box>
        <Divider />
        <Grid container spacing={4} className={classes.gridContainer}>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={smDown ? 'center' : 'start'}>
              <Avatar alt={user?.name}>{avatarLetters(user?.name)}</Avatar>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                ml={2}
                maxWidth="100%"
                width={0}
                flexGrow={1}
              >
                <Tooltip title={user?.name} placement="top-start">
                  <Typography
                    fontWeight={700}
                    color="primary"
                    variant="subtitle1"
                    noWrap
                  >
                    {user?.name}
                  </Typography>
                </Tooltip>
                {subTitle && (
                  <Tooltip title={subTitle} placement="top-start">
                    <Typography variant="caption" color="textSecondary" noWrap>
                      {subTitle}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" justifyContent={smDown ? 'center' : 'end'}>
              <Button
                variant="outlined"
                component="label"
                className={classes.errorColor}
                startIcon={<PaperclipIcon size={14} />}
                disabled={disabled}
                fullWidth
              >
                ANEXAR
                <Box
                  display="none"
                  component="input"
                  type="file"
                  multiple
                  aria-label="Selecionar arquivo"
                  accept={constants.validations.ALL_FILE_TYPES}
                  onChange={handleFileChange}
                />
              </Button>
              <Controller
                as={<></>}
                control={control}
                name={attachmentControlName}
              />
            </Box>
          </Grid>
          {!isEmpty(buttonItems) && (
            <Grid item xs={12} md={6} className={classes.buttonGroupContainer}>
              <Box width="100%">
                <Controller
                  as={
                    <ButtonGroup
                      options={buttonItems}
                      controlName={controlName}
                      title="Enviar como"
                      type="submit"
                      form={form}
                      fullWidth
                      disabled={disabled}
                    />
                  }
                  control={control}
                  name={controlName}
                />
              </Box>
              {infoTooltip && (
                <Box alignContent="center">
                  <Tooltip title={infoTooltip}>
                    <IconButton size="small" className={classes.infoIconButton}>
                      <Info size={20} color={theme.palette.info.main} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
        {deadlineMessage && !disabled && (
          <Box className={classes.deadlineBox}>
            <ClockIcon color="white" size={16} />
            <Typography variant="body2">{deadlineMessage}</Typography>
          </Box>
        )}
      </Paper>
      {!!errors?.[attachmentControlName] && (
        <FormHelperText error={!!errors?.[attachmentControlName]}>
          {<>{errors?.[attachmentControlName]?.message}</>}
        </FormHelperText>
      )}
    </Box>
  )
}

TextBox.propTypes = {
  form: PropTypes.string,
  attachmentControlName: PropTypes.string,
  controlName: PropTypes.string,
  buttonItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    }),
  ),
  common: PropTypes.bool,
  subTitle: PropTypes.string,
  deadlineMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  infoTooltip: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
}

export default TextBox
